import React, { useState } from 'react';
import './App.css'; // Assuming you have a basic App.css or will add Bootstrap via index.html/main.tsx

function App() {
  const [url, setUrl] = useState('');
  const [summary, setSummary] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(''); // State for copy feedback

  // Basic Markdown to HTML converter for rich text display
  const renderMarkdown = (markdownText: string) => {
    let html = markdownText;

    // Convert bold text (**text**)
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // Convert list items (* item)
    html = html.replace(/^(?:\*|\-)\s+(.*)$/gm, '<li>$1</li>');
    if (html.includes('<li>')) {
      html = `<ul>${html}</ul>`;
    }
    // Convert newlines to <br> for general text, but not inside <ul> or <li>
    html = html.replace(/(?<!<\/li>)(?<!<\/ul>)\n/g, '<br />');

    return <div dangerouslySetInnerHTML={{ __html: html }} />;
  };

  const handleSummarize = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    setSummary('');
    setError('');
    setCopySuccess(''); 

    if (!url) {
      setError('Please provide a URL.');
      setIsLoading(false);
      return;
    }

    try {
      // Step 1: Fetch content from the provided URL using the corsproxy
      const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(url)}`;
      const webpageResponse = await fetch(proxyUrl);

      if (!webpageResponse.ok) {
        throw new Error(`Failed to fetch webpage: ${webpageResponse.statusText}`);
      }

      const webpageContent = await webpageResponse.text();

      // *** NEW: Extract plain text from HTML content ***
      // This is a basic approach. For more complex HTML parsing, consider a server-side solution with 'jsdom' or 'cheerio'.
      let plainTextContent = webpageContent
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove script tags
        .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')   // Remove style tags
        .replace(/<[^>]*>/g, '') // Remove all other HTML tags
        .replace(/\s+/g, ' ') // Replace multiple spaces/newlines with a single space
        .trim(); // Trim leading/trailing whitespace

      // Limit the text content sent to AI to avoid token limits and focus on relevant parts
      const maxTextLength = 8000;
      if (plainTextContent.length > maxTextLength) {
        plainTextContent = plainTextContent.substring(0, maxTextLength) + '... [Content truncated]';
      }

      // Step 2: Send the plain text content to the AI endpoint for summarization
      const aiPrompt = `Summarize the following company's business, services, and mission based on the provided text. Focus on key information about the company itself, not the webpage structure:\n\n${plainTextContent}`; // Use plainTextContent

      // Using the user-provided AI endpoint with corsproxy
      const aiResponse = await fetch("https://corsproxy.io/?https://llm.zithera.com.au/api/generate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": "cZaV6jf703FjFGfv5IYEpXlVS70iBlYVLNhcRRB4dm0bppqQOY517Zh5uMEPOytUVAEc5k89Awsahfaw",
        },
        body: JSON.stringify({
          model: "llama3.2", // Using the model specified by the user
          prompt: aiPrompt,
          Stream: false, // Explicitly set to false as per your request
        }),
      });

      if (!aiResponse.ok) {
        const errorText = await aiResponse.text();
        console.error('AI API Error Response (Raw Text):', errorText);
        throw new Error(`AI summarization failed: ${aiResponse.statusText}. Details: ${errorText.slice(0, 200)}...`);
      }

      // Read the full JSON response directly, as Stream: false is used
      const aiData = await aiResponse.json();
      console.log('Full AI Response Data:', aiData); // Log the complete JSON response

      // Extract summary from aiData.response field
      const generatedSummary = aiData.response || 'No summary found in AI response.';
      setSummary(generatedSummary);

    } catch (err) {
      console.error('Error summarising company:', err);
      setError(`An error occurred: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to handle copying summary to clipboard
  const handleCopySummary = () => {
    if (summary) {
      navigator.clipboard.writeText(summary)
        .then(() => {
          setCopySuccess('Copied!');
          setTimeout(() => setCopySuccess(''), 2000); // Clear message after 2 seconds
        })
        .catch(err => {
          console.error('Failed to copy text: ', err);
          setCopySuccess('Failed to copy!');
        });
    }
  };

  return (
    <div className="d-flex justify-content-center align-items-center min-vh-100 bg-light p-4">
      <div className="card shadow-lg" style={{ maxWidth: '600px', width: '100%' }}>
        <div className="card-header text-center">
          <h5 className="card-title mb-0">Company Summariser</h5>
        </div>
        <div className="card-body">
          <form onSubmit={handleSummarize} className="mb-4">
            <div className="mb-3">
              <label htmlFor="linkedin-url" className="form-label">
                LinkedIn OR Company URL
              </label>
              <input
                id="linkedin-url"
                name="url"
                type="url"
                placeholder="e.g., https://www.linkedin.com/company/zithera-it-consulting/ OR https://zithera.com.au/"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                required
                className="form-control"
              />
            </div>
            <button type="submit" className="btn btn-primary w-100" disabled={isLoading}>
              {isLoading ? 'Summarising...' : 'Get Summary'}
            </button>
          </form>

          {summary && (
            <div className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">Summary:</h6>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={handleCopySummary}
              >
                {copySuccess || 'Copy Summary'}
              </button>
            </div>
            {/* Summary display area - now using a div for rich text */}
            <div
              className={`form-control p-3 ${error ? 'border-danger' : 'border-success'}`}
              style={{ minHeight: '250px', backgroundColor: '#f8f9fa', overflowY: 'auto' }}
              aria-label="Company summary"
            >
              {renderMarkdown(summary)}
            </div>
          </div>
          )}
          {error && (
            <p className="text-danger mt-2">
              Error: {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
